import axios from "axios";
import {saveAs} from "file-saver";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import * as API_URL from "../../constants/ApiUrl";
import * as QUERY from "../../constants/query/Notice.query";
import * as Routes from "../../constants/Routes";
import List from "../common/list/List";
import {InitialListProps, ListColumnType, ListProps} from "../common/list/List.props";
import Pagination from "../common/pagination/Pagination";
import Popup from "../common/popup/Popup";
import {PopupProps} from "../common/popup/Popup.props";
import Toast from "../common/toast/Toast";
import * as NOTICE_LIST_PROPS from "./constants/NoticeList.props";
import {NoticeDto} from "./dto/Notice.dto";
import "./style.css";
import {InitialPaginationProps, PaginationProps} from "../common/pagination/Pagination.props";
import SearchForm from "../common/searchForm/SearchForm";
import {NoticeSearchFormProps} from "./constants/SearchForm.props";
import {Option} from "../common/searchForm/Option";

export default function Notice() {
    const navigate = useNavigate();

    const searchProps = NoticeSearchFormProps;
    const [listProps, setListProps] = useState<ListProps<NoticeDto>>(InitialListProps);
    const [paginationProps, setPaginationProps] = useState<PaginationProps>(InitialPaginationProps);
    const [popupProps, setPopupProps] = useState<PopupProps | null>(null);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [toasts, setToasts] = useState<any[]>([]);

    useEffect(() => {
        setListProps({
            headers: [...NOTICE_LIST_PROPS.HEADERS, {
                type: ListColumnType.BUTTON,
                name: "",
                key: "",
                button: {
                    name: "상세 보기",
                    onClick: (seq: number) => handleEditButtonClick(seq)
                }
            }],
            items: [],
            emptyProps: NOTICE_LIST_PROPS.EMPTY_PROPS(handleAddButtonClick),
            onCheckedChange: (seqs: number[]) => setItemChecked(seqs)
        });
        setPaginationProps(prevState => ({
            ...prevState,
            onChange: handleSetPage
        }));
        searchProps.onSearch = () => getPage();
        searchProps.onUpdate = (options: Option[]) => {
            searchProps.options = options;
        }
    }, []);

    useEffect(() => {
        getPage();
    }, [paginationProps.currentPage]);

    const getPage = () => {
        axios.post(API_URL.GRAPHQL, {query: QUERY.GET_NOTICE(searchProps.options[0].value, paginationProps.currentPage)})
            .then(response => {
                const paginationDto = response.data.data.getNotice;

                if (paginationDto.items.length === 0 && paginationDto.currentPage > 0) {
                    handleSetPage(paginationDto.currentPage - 1);
                } else {
                    setTotalCount(paginationDto.totalItems);
                    setPaginationProps(prevState => ({
                        ...prevState,
                        currentPage: paginationDto.currentPage,
                        totalPage: paginationDto.totalPage
                    }));
                    setListProps(prevState => ({
                        ...prevState,
                        items: paginationDto.items
                    }));
                }
            });
    }

    const handleSetPage = (page: number) => {
        setPaginationProps(prevState => ({
            ...prevState,
            currentPage: page
        }));
    }

    const setItemChecked = (seqs: number[]) => {
        setListProps(prevState => ({
            ...prevState,
            items: prevState.items.map(item => {
                item.checked = seqs.includes(item.seq!);
                return item;
            })
        }));
    }

    const handleDeleteButtonClick = () => {
        const checkedItems = listProps.items.filter(item => item.checked);

        if (checkedItems.length === 0) {
            setPopupProps({
                subject: "공지사항 삭제",
                message1: "삭제할 항목을 선택해 주세요.",
                message2: null,
                buttonName: "확인",
                onConfirm: () => setPopupProps(null),
                useCloseButton: false,
                onClose: () => {}
            });
        } else {
            setPopupProps({
                subject: "공지사항 삭제",
                message1: "선택한 공지사항을 삭제하시겠습니까?",
                message2: null,
                buttonName: "삭제",
                onConfirm: () => deleteNotices(checkedItems.map(item => item.seq)),
                useCloseButton: false,
                onClose: () => {}
            });
        }
    }

    const deleteNotices = (seqs: (number | null)[]) => {
        setPopupProps(null);

        axios.post(API_URL.DELETE_NOTICE, seqs)
            .then(() => {
                setToasts(prevState => [...prevState, []]);
                getPage();
            });
    }

    const handleToastClose = () => {
        setToasts([...toasts].splice(0, 1));
    }

    const handleAddButtonClick = () => {
        navigate(Routes.NOTICE_WRITE);
    }

    const handleEditButtonClick = (seq: number) => {
        navigate(Routes.NOTICE_WRITE, {state: {seq: seq}});
    }

    const handleSaveAllButtonClick = () => {
        axios.post(API_URL.EXPORT_NOTICES_TO_EXCEL, [], {responseType: "blob"})
            .then(response => {
                saveAs(response.data, "공지사항 목록.xlsx");
            });
    }

    const handleSaveButtonClick = () => {
        const seqs = listProps.items.filter(item => item.checked).map(item => item.seq);

        if (seqs.length === 0) {
            setPopupProps({
                subject: "공지사항 엑셀 저장",
                message1: "저장할 항목을 선택해 주세요.",
                message2: null,
                buttonName: "확인",
                onConfirm: () => setPopupProps(null),
                useCloseButton: false,
                onClose: () => {}
            });

            return;
        }

        axios.post(API_URL.EXPORT_NOTICES_TO_EXCEL, seqs, {responseType: "blob"})
            .then(response => {
                saveAs(response.data, "공지사항 목록.xlsx");
            });
    }

    return (
        <>
            <main className="notice-add-wrapper">
                <div className="notice-body">
                    <section className="list-search-section">
                        <div className="title">
                            공지사항 관리
                        </div>
                        <SearchForm {...searchProps}/>
                    </section>
                    <div className="notice-numbers">
                        <div className="notice-wrapper">
                            <div className="notice-all-number" data-testid="totalItems">
                                전체 공지 개수 {totalCount}개
                            </div>
                            <div className="notice-choice">
                                <button className="save" data-testid="saveAllButton" onClick={handleSaveAllButtonClick}>
                                    전체 엑셀저장
                                </button>
                                <button className="save" onClick={handleSaveButtonClick}>
                                    선택항목 엑셀저장
                                </button>
                                <button className="save" data-testid="deleteButton" onClick={handleDeleteButtonClick}>
                                    선택항목 일괄삭제
                                </button>
                                <button className="delete" data-testid="addButton" onClick={handleAddButtonClick}>
                                    공지사항 추가하기
                                </button>
                            </div>
                        </div>
                        <List {...listProps}/>
                    </div>
                    <Pagination {...paginationProps}/>
                </div>
            </main>
            {popupProps && <Popup data={popupProps}/>}
            {
                toasts.map((_, index) => (
                    <Toast key={index} message="공지사항이 삭제되었습니다." onClose={handleToastClose}/>
                ))
            }
        </>
    );
}